import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SearchInput from '../../../../shared/chat-bar/search-input';
import { useTheme } from '../../../../theme-context';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { store } from '../../../../app/store';
import { MEMBER_STATUS } from '../../../../utils/constants/member-status';
import {
    tryToAddActionForChat,
    tryToFetchSingleChat,
} from '../../../../store/brokers/partners/history-chats/historyChatsSlice';
import { useSignUpWithAgentMutation } from '../../../../store/user/userApi';
import type {
    ChatUiAction,
    ChatUiMessage,
} from '../../../../store/brokers/partners/chats/ChatsUISlice';
import TeamsModal from '../../../../components/teams/TeamsModal';
import ReportIssuesModal from '../../../../components/report-issue/ReportIssueModal';
import AiMessages from '../../../../shared/chat-messages/ai-messages';
import ClientMessages from '../../../../shared/chat-messages/client-messages';

type Props = {
    onClose: () => void;
};

function mapSignUpChatDataToUi(rows: unknown): ChatUiMessage[] {
    if (!Array.isArray(rows)) return [];
    return rows.map((row: any) => {
        if (row?.user) {
            return {
                user: {
                    ...row.user,
                    createdAt: row.user.createdAt
                        ? new Date(row.user.createdAt)
                        : new Date(),
                },
            };
        }
        if (row?.agent) {
            return { agent: { ...row.agent } };
        }
        return row;
    });
}

/**
 * Unwraps RTK/axios shapes down to the payload object that may contain either:
 * - `chatData`: conversation array, or
 * - flat agent fields: `message`, `topic`, `stage`, `agentMessageId` (no chatData)
 */
function getSignUpResponsePayload(result: unknown): Record<string, unknown> {
    const r = result as Record<string, unknown>;
    if (!r || typeof r !== 'object') return {};

    const body = (r.data as Record<string, unknown>) ?? r;

    // { success, status, data: { topic, message, ... } }
    if (
        body &&
        typeof body.data === 'object' &&
        body.data !== null &&
        !Array.isArray(body.data) &&
        ('message' in (body.data as object) ||
            'chatData' in (body.data as object) ||
            'topic' in (body.data as object))
    ) {
        return body.data as Record<string, unknown>;
    }

    // { data: { chatData, metadata } } from older handlers
    if (
        body &&
        typeof body.data === 'object' &&
        body.data !== null &&
        'chatData' in (body.data as object)
    ) {
        return body.data as Record<string, unknown>;
    }

    if ('message' in body || 'chatData' in body || 'topic' in body) {
        return body;
    }

    return body;
}

/**
 * Builds UI messages from sign-up-by-admin response.
 * When the API returns a flat agent object (no chatData array), appends one agent turn to `seedMessages`.
 */
function parseSignUpAgentResponse(
    result: unknown,
    seedMessages?: ChatUiMessage[],
): { messages: ChatUiMessage[]; topicId?: string } {
    const payload = getSignUpResponsePayload(result);

    const topicId =
        (payload.topic as string | undefined) ??
        ((payload.metadata as Record<string, unknown>)?.chatId as
            | string
            | undefined) ??
        (payload.chatId as string | undefined) ??
        ((payload.metadata as Record<string, unknown>)?.topic as
            | string
            | undefined);

    const chatData = payload.chatData as unknown[] | undefined;
    if (Array.isArray(chatData) && chatData.length > 0) {
        return { messages: mapSignUpChatDataToUi(chatData), topicId };
    }

    const message = payload.message as string | undefined;
    const stage = payload.stage as string | undefined;
    const agentMessageId = payload.agentMessageId as string | undefined;
    const options = payload.options as unknown;

    if (message != null && message !== '') {
        const agentEntry: ChatUiMessage = {
            agent: {
                message,
                stage,
                _id: agentMessageId,
                options,
                createdAt: new Date(),
            },
        };
        const base = seedMessages?.length ? [...seedMessages] : [];
        return { messages: [...base, agentEntry], topicId };
    }

    return { messages: seedMessages ?? [], topicId };
}

const ExecChatDrawer: React.FC<Props> = ({ onClose }) => {
    const dispatch = useAppDispatch();
    const [signUpAgent] = useSignUpWithAgentMutation();
    const { theme } = useTheme();
    const {
        chatId: historyId,
        action: selectedAction,
        chatData,
    } = useAppSelector((s) => s.chatUi);

    const [chatId, setChatId] = useState<string>();
    const [isTyping, setIsTyping] = useState(false);
    const [allMessages, setAllMessages] = useState<
        ChatUiMessage[] | undefined
    >();
    const [isWaiting, setIsWaiting] = useState(false);
    const [showTeamsModal, setShowTeamsModal] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<any>();
    const [reportModal, setReportModal] = useState<{
        show: boolean;
        messageId: string;
    }>();
    const scrollableRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const hasInitialized = useRef(false);

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        section: string,
    ) => {
        const files = e.target.files;
        if (!files) return;
        setUploadedFiles((prev: any) => ({
            ...prev,
            [section]: [...(prev?.[section] || []), ...Array.from(files)],
        }));
    };

    const removeFile = (section: string, index: number) => {
        setUploadedFiles((prev: any) => ({
            ...prev,
            [section]: prev[section].filter((_: any, i: number) => i !== index),
        }));
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return () => clearTimeout(timeout);
    }, [allMessages, isTyping, isWaiting]);

    const onGettingChatHistory = async () => {
        if (!historyId) return;
        try {
            const response = await dispatch(
                tryToFetchSingleChat(historyId),
            ).unwrap();
            const formatData = response?.data?.map((item: any) => {
                if (item?.agent) return { agent: { ...item, ...item.agent } };
                return { user: { ...item, ...item.user } };
            });
            setAllMessages(formatData);
        } catch {
            setAllMessages(chatData || undefined);
        }
    };

    const initializeExecChat = async (
        seed: ChatUiMessage[] | undefined,
        act: ChatUiAction | undefined,
    ) => {
        setIsWaiting(true);
        try {
            const firstMsg = seed?.[0]?.user?.message?.trim();

            if (act) {
                const message = firstMsg || act.text;
                const userThread: ChatUiMessage[] =
                    seed?.length && seed.length > 0
                        ? seed
                        : [
                              {
                                  user: {
                                      message,
                                      files: [],
                                      sort: 2,
                                      createdAt: new Date(),
                                  },
                              },
                          ];

                const result = await signUpAgent({
                    actionSelected: act.enum,
                    message,
                    files: [],
                }).unwrap();

                const { messages, topicId } = parseSignUpAgentResponse(
                    result,
                    userThread,
                );
                setAllMessages(messages);
                if (topicId) setChatId(topicId);
                return;
            }

            if (firstMsg) {
                const result = await signUpAgent({
                    message: firstMsg,
                    files: [],
                }).unwrap();

                const { messages, topicId } = parseSignUpAgentResponse(
                    result,
                    seed as ChatUiMessage[],
                );
                setAllMessages(messages);
                if (topicId) setChatId(topicId);
                return;
            }

            const result = await signUpAgent({
                message: '',
                files: [],
            }).unwrap();

            const { messages, topicId } = parseSignUpAgentResponse(result, []);
            setAllMessages(messages);
            if (topicId) setChatId(topicId);
        } catch (err) {
            console.error(err);
        } finally {
            setIsWaiting(false);
        }
    };

    useEffect(() => {
        if (historyId) {
            setChatId(historyId);
            onGettingChatHistory().catch(() => undefined);
            return;
        }
        if (hasInitialized.current) return;
        hasInitialized.current = true;
        const { chatData: cd, action: act } = store.getState().chatUi;
        initializeExecChat(cd, act as ChatUiAction | undefined).catch(
            () => undefined,
        );
    }, [historyId]);

    const onGetChatResponse = async (userMessage: ChatUiMessage) => {
        const updated = allMessages
            ? [...allMessages, userMessage]
            : [userMessage];
        setAllMessages(updated);

        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);

        setIsWaiting(true);

        const lastAgentTurn = [...(allMessages ?? [])]
            .reverse()
            .find((m) => m.agent)?.agent;
        const files = (userMessage?.user?.files as unknown[]) ?? [];

        const payload: Record<string, unknown> = {
            topic: chatId,
            message: userMessage?.user?.message,
            files,
        };

        if (lastAgentTurn?.stage) {
            payload.stage = lastAgentTurn.stage;
        }

        if (selectedAction?.enum) {
            payload.actionSelected = selectedAction.enum;
        }

        try {
            const result = await signUpAgent(payload as any).unwrap();
            const { messages, topicId } = parseSignUpAgentResponse(
                result,
                updated as ChatUiMessage[],
            );
            setAllMessages(messages);
            if (topicId) setChatId(topicId);
        } catch (error) {
            console.error('Failed to send chat message:', error);
        } finally {
            setIsWaiting(false);
        }
    };

    const onClickMessageAction = async (msgAction: string, id: string) => {
        if (msgAction === 'report') {
            setReportModal({
                show: true,
                messageId: id,
            });
            return;
        }
        try {
            await dispatch(
                tryToAddActionForChat({ id, data: { action: msgAction } }),
            ).unwrap();
            const formatAllMessages = allMessages?.map((item: any) => {
                if (item?.agent?._id === id) {
                    const key =
                        msgAction === 'like'
                            ? 'liked'
                            : msgAction === 'dislike'
                            ? 'disliked'
                            : 'favorite';
                    if (key === 'liked')
                        return {
                            agent: {
                                ...item.agent,
                                liked: !item.agent.liked,
                                disliked: false,
                            },
                        };
                    if (key === 'disliked')
                        return {
                            agent: {
                                ...item.agent,
                                disliked: !item.agent.disliked,
                                liked: false,
                            },
                        };
                    return {
                        agent: { ...item.agent, [key]: !item.agent[key] },
                    };
                }
                return item;
            });
            setAllMessages(formatAllMessages);
        } catch (err) {
            console.error(err);
        }
    };

    const onClickLinks = (type: string) => {
        if (type === 'teams') setShowTeamsModal(true);
    };

    const onCloseLinksModals = () => setShowTeamsModal(false);

    const onSuccessfullyDeleted = () => {
        onGetChatResponse({
            user: {
                message: "I'm done with offboarding thank you",
                files: [],
            },
        });
    };

    const onClickOneOption = (option: any) => {
        onGetChatResponse({
            user: {
                message: `${option?.key ? `${option.key}. ` : ''}${
                    option?.value
                }`,
                files: [],
            },
        });
    };

    const onSuccessReport = () => {
        onGetChatResponse({
            user: {
                message: '*Reported an issue.',
                files: [],
            },
        });
    };

    const onCloseReportModal = () => {
        setReportModal(undefined);
    };

    return (
        <AnimatePresence>
            <motion.div
                layoutId="exec-claire-chat"
                className="relative z-20 w-full min-h-screen bg-[#F5F5F5] dark:bg-bgColor-darkMode flex flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}>
                <header className="w-full border-b border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-bgColor-darkMode">
                    <div className="flex w-full items-center px-6 py-3">
                        <div className="flex flex-row items-center gap-2">
                            <div className="h-[28px] w-[5px] rounded-full bg-[#00330F] dark:bg-[#00691F]" />
                            <div className="flex flex-row items-center bg-[#F7F9FB] dark:bg-black px-2 rounded-[8px] h-[28px]">
                                <p className="dark:text-primary-darkMode mr-2 font-inter text-[14px] font-semibold">
                                    Claire
                                </p>
                                <p className="text-sm font-inter text-[14px] font-normal text-[#A4A7AE]">
                                    AI Reservation Manager
                                </p>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="mx-auto mt-5 mb-2 w-full max-w-[90%] min-w-[90%] flex justify-end">
                    <button
                        type="button"
                        className="cursor-pointer hover:opacity-80"
                        onClick={onClose}
                        aria-label="Close chat">
                        <img
                            src={
                                theme === 'dark'
                                    ? '/assets/shared/close-icon-white.svg'
                                    : '/assets/shared/close-icon.svg'
                            }
                            className="object-contain w-[25px]"
                            alt=""
                        />
                    </button>
                </div>

                <div
                    ref={scrollableRef}
                    className="flex-1 overflow-y-auto px-4 py-2 max-w-[90%] min-w-[90%] mx-auto border-2 border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-[#3a3a3a] rounded-2xl">
                    {allMessages?.map((item: any, idx: number) => {
                        const templateFiles =
                            item?.agent?.stage === 'upload_template_files'
                                ? (allMessages &&
                                      allMessages[idx + 1]?.user?.files) ||
                                  null
                                : null;

                        return (
                            <div key={idx}>
                                {item?.agent ? (
                                    <div className="flex flex-row items-start">
                                        {(idx === 0 ||
                                            !allMessages[idx - 1]?.agent
                                                ?.message) && (
                                            <div className="mt-4 mr-2">
                                                <img
                                                    src="/assets/agents/claire-agent-icon.svg"
                                                    className="min-w-[35px] rounded-full"
                                                    alt=""
                                                />
                                            </div>
                                        )}
                                        <AiMessages
                                            text={item.agent.message}
                                            files={item.agent.files}
                                            isNew={
                                                allMessages.length - 1 === idx
                                            }
                                            data={item.agent}
                                            setIsTyping={setIsTyping}
                                            isTyping={
                                                isTyping &&
                                                allMessages.length - 1 === idx
                                            }
                                            showActions
                                            considerDarkMode
                                            uploadedFiles={uploadedFiles}
                                            templateFiles={templateFiles}
                                            handleFileChange={handleFileChange}
                                            removeFile={removeFile}
                                            onClickAction={onClickMessageAction}
                                            onClickLinks={onClickLinks}
                                            onClickOptions={onClickOneOption}
                                        />
                                    </div>
                                ) : (
                                    <ClientMessages
                                        color="bg-[#E9EAEB] !text-black"
                                        text={item.user.message}
                                        time={item.user.createdAt}
                                        considerDarkMode
                                        stage={item?.user?.stage}
                                        files={item.user.files}
                                    />
                                )}
                            </div>
                        );
                    })}

                    {isWaiting && (
                        <div className="flex items-start gap-2 mt-3">
                            <img
                                src="/assets/agents/claire-agent-icon.svg"
                                className="min-w-[35px] rounded-full"
                                alt=""
                            />
                            <div className="text-gray-400 py-2 px-3 rounded-xl max-w-[85%] text-sm animate-pulse">
                                Claire is preparing your answer...
                            </div>
                        </div>
                    )}

                    <div ref={bottomRef} />
                </div>

                <div className="sticky bottom-0 w-full py-4 bg-[#F5F5F5] dark:bg-bgColor-darkMode">
                    <div className="mx-auto w-full max-w-[90%] min-w-[90%]">
                        <SearchInput
                            variant="rebecca"
                            rebeccaFormMaxWidthClass=""
                            placeholder="Ask Claire something"
                            chatData={allMessages}
                            chatId={chatId}
                            setUploadedTemplateFiles={setUploadedFiles}
                            uploadedTemplateFiles={uploadedFiles}
                            considerDarkMode
                            stage={
                                allMessages && allMessages.length > 0
                                    ? allMessages[allMessages.length - 1]?.agent
                                          ?.stage
                                    : undefined
                            }
                            options={
                                allMessages && allMessages.length > 0
                                    ? allMessages[allMessages.length - 1]?.agent
                                          ?.options
                                    : undefined
                            }
                            onClickOptions={onClickOneOption}
                            onSendingData={onGetChatResponse}
                            isWaiting={isWaiting || isTyping}
                        />
                    </div>
                </div>
            </motion.div>

            {showTeamsModal && (
                <TeamsModal
                    openModal={showTeamsModal}
                    status={MEMBER_STATUS.find(
                        (item: any) => item?.value === 'live',
                    )}
                    handleCloseModal={onCloseLinksModals}
                    SuccessfullyDeleted={onSuccessfullyDeleted}
                />
            )}
            {reportModal?.show && (
                <ReportIssuesModal
                    openModal={reportModal?.show}
                    messageId={reportModal?.messageId || ''}
                    chatId={chatId || ''}
                    onSuccessReport={onSuccessReport}
                    handleCloseModal={onCloseReportModal}
                />
            )}
        </AnimatePresence>
    );
};

export default ExecChatDrawer;
